import { Component, OnInit } from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  RoutesRecognized,
} from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { filter, first, pairwise } from 'rxjs/operators';

import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AuthenticationService, VerificationMethods } from '@app/_services';
import { getMaxListeners } from 'process';

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.css'],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  verificationForm: FormGroup;
  totpVerifyForm: FormGroup;
  totpSetupForm: FormGroup;
  loading = false;
  submitted = false;
  accountCreated: boolean;
  passwordReset: boolean;
  returnUrl: string;
  error = '';
  passwordVisible = false;
  password?: string;
  private history = [];

  // Verification state
  requiresVerification = false;
  requiresTOTP = false;
  verificationError = '';
  verificationMethods: VerificationMethods | null = null;
  trustDevice = true;
  resendDisabled = false;
  resendCountdown = 0;
  deviceType: 'desktop' | 'mobile' | null = null;
  replacingDevice: string | null = null;

  // 2FA Setup state (forced after email verification)
  requires2FASetup = false;
  setupFlowToken: string | null = null;
  setupData: {
    qr_code: string;
    secret: string;
    setup_token: string;
  } | null = null;
  setupError = '';
  backupCodes: string[] = [];
  showBackupCodes = false;
  backupCodesCopied = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private http: HttpClient
  ) { }

  ngOnInit() {
    if (localStorage.getItem('accountCreated') == '1') {
      this.accountCreated = true;
      document.body.scrollTop = document.documentElement.scrollTop = 0;
      localStorage.removeItem('accountCreated');
    }
    if (localStorage.getItem('passwordReset') == '1') {
      this.passwordReset = true;
      document.body.scrollTop = document.documentElement.scrollTop = 0;
      localStorage.removeItem('passwordReset');
    }

    this.loginForm = this.formBuilder.group({
      username: [null, [Validators.required]],
      password: [null, [Validators.required]],
    });

    // Verification form (for email code)
    this.verificationForm = this.formBuilder.group({
      code: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(9)]],
      trustDevice: [true]
    });

    // TOTP verification form (for authenticator code after email verified)
    this.totpVerifyForm = this.formBuilder.group({
      code: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });

    // TOTP setup form (for initial 2FA setup)
    this.totpSetupForm = this.formBuilder.group({
      code: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });


    // get return url from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  // Getter for verification form
  get vf() {
    return this.verificationForm.controls;
  }

  // Getter for TOTP setup form
  get sf() {
    return this.totpSetupForm.controls;
  }

  // Getter for TOTP verify form
  get tvf() {
    return this.totpVerifyForm.controls;
  }

  onSubmit() {
    this.submitted = true;

    for (const i in this.loginForm.controls) {
      this.loginForm.controls[i].markAsDirty();
      this.loginForm.controls[i].updateValueAndValidity();
    }

    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.error = '';

    console.log('Starting login...');

    this.authenticationService
      .login(this.f.username.value, this.f.password.value)
      .pipe(first())
      .subscribe({
        next: (data) => {
          console.log('Login response:', data);

          if (data.requires_verification) {
            console.log('Requires verification, showing verification form...');
            this.requiresVerification = true;
            this.verificationMethods = data.verification_methods;
            this.deviceType = data.device_type;
            this.replacingDevice = data.replacing_device;
            this.loading = false;
            this.startResendCooldown();
            setTimeout(() => {
              const codeInput = document.getElementById('verificationCode');
              if (codeInput) {
                codeInput.focus({ preventScroll: true });
              }
            }, 100);
          } else {
            console.log('Login successful, redirecting...');
            window.location.href = this.returnUrl;
          }
        },
        error: (error) => {
          console.log('Login error:', error);
          console.log('Error status:', error?.status);
          console.log('Error body:', error?.error);
          this.loading = false;

          // Check for device replacement limit error (403)
          if (error?.status === 403 && error?.error?.error === 'device_replacement_limit') {
            this.error = error.error.message;
            return;
          }

          // Handle other API errors
          if (error?.error?.message) {
            if (error.error.message.includes('bad password')) {
              this.error = 'Email sau parola gresite! Sau nu ai cont? Înregistrează-te!';
            } else {
              this.error = error.error.message;
            }
            return;
          }

          // Handle string errors
          if (typeof error === 'string') {
            if (error.includes('bad password')) {
              this.error = 'Email sau parola gresite! Sau nu ai cont? Înregistrează-te!';
            } else {
              this.error = error;
            }
            return;
          }

          // Fallback
          this.error = 'A apărut o eroare. Încercați din nou.';
        },
        complete: () => {
          console.log('Login observable completed');
        }
      });
  }

  // Submit verification code (email)
  onSubmitVerification() {
    for (const i in this.verificationForm.controls) {
      this.verificationForm.controls[i].markAsDirty();
      this.verificationForm.controls[i].updateValueAndValidity();
    }

    if (this.verificationForm.invalid) {
      return;
    }

    this.loading = true;
    this.verificationError = '';

    this.authenticationService
      .verifyDevice(this.vf.code.value, this.vf.trustDevice.value)
      .pipe(first())
      .subscribe(
        (data) => {
          console.log('Verify response:', data);

          if (data.requires_2fa_setup) {
            // No TOTP yet - go to setup
            this.requiresVerification = false;
            this.requires2FASetup = true;
            this.setupFlowToken = data.setup_flow_token;
            this.setupData = data.setup_data;
            this.loading = false;
          } else if (data.requires_totp) {
            // Has TOTP - need to enter TOTP code now
            this.requiresVerification = false;
            this.requiresTOTP = true;
            this.loading = false;
            // Clear code input for TOTP entry
            this.verificationForm.patchValue({ code: '' });
            setTimeout(() => {
              const codeInput = document.getElementById('verificationCode');
              if (codeInput) codeInput.focus();
            }, 100);
          } else {
            // Fully verified - go to app
            window.location.href = this.returnUrl;
          }
        },
        (error) => {
          console.log('Verification error:', error);
          let errorMessage = 'Cod invalid. Vă rugăm să încercați din nou.';
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error?.error?.error) {
            errorMessage = error.error.error;
          }
          this.verificationError = errorMessage;
          this.loading = false;
          this.verificationForm.patchValue({ code: '' });
        }
      );
  }

  // Submit TOTP setup verification code
  onSubmitTOTPSetup() {
    for (const i in this.totpSetupForm.controls) {
      this.totpSetupForm.controls[i].markAsDirty();
      this.totpSetupForm.controls[i].updateValueAndValidity();
    }

    if (this.totpSetupForm.invalid || !this.setupFlowToken) {
      return;
    }

    this.loading = true;
    this.setupError = '';

    this.authenticationService
      .complete2FASetupFlow(this.setupFlowToken, this.sf.code.value)
      .pipe(first())
      .subscribe(
        (data) => {
          // Option 1: Skip backup codes display and redirect directly
          window.location.href = this.returnUrl;

          // Option 2: Show backup codes (commented out - keep for future use)
          // this.backupCodes = data.backup_codes;
          // this.showBackupCodes = true;
          // this.loading = false;
        },
        (error) => {
          this.setupError = 'Cod invalid. Verificați că ați scanat codul QR corect și încercați din nou.';
          this.loading = false;
          this.totpSetupForm.patchValue({ code: '' });
        }
      );
  }

  // Copy backup codes to clipboard
  copyBackupCodes() {
    const text = this.backupCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      this.backupCodesCopied = true;
      setTimeout(() => {
        this.backupCodesCopied = false;
      }, 3000);
    });
  }

  // Submit TOTP code (after email was verified)
  onSubmitTOTPVerify() {
    for (const i in this.totpVerifyForm.controls) {
      this.totpVerifyForm.controls[i].markAsDirty();
      this.totpVerifyForm.controls[i].updateValueAndValidity();
    }

    if (this.totpVerifyForm.invalid) {
      return;
    }

    this.loading = true;
    this.verificationError = '';

    // Call verifyDevice again with the TOTP code
    this.authenticationService
      .verifyDevice(this.tvf.code.value, true)
      .pipe(first())
      .subscribe(
        (data) => {
          console.log('TOTP verify response:', data);
          // Should be fully authenticated now
          window.location.href = this.returnUrl;
        },
        (error) => {
          console.log('TOTP verification error:', error);
          let errorMessage = 'Cod invalid. Verificați și încercați din nou.';
          if (typeof error === 'string') {
            errorMessage = error;
          }
          this.verificationError = errorMessage;
          this.loading = false;
          this.totpVerifyForm.patchValue({ code: '' });
        }
      );
  }

  // Download backup codes as text file
  downloadBackupCodes() {
    const text = 'Doctor Business - Coduri de rezervă 2FA\n' +
      '========================================\n\n' +
      'Păstrați aceste coduri în siguranță. Fiecare cod poate fi folosit o singură dată.\n\n' +
      this.backupCodes.join('\n');

    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'doctor-business-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // Continue to app after saving backup codes
  continueToApp() {
    window.location.href = this.returnUrl;
  }

  // Resend verification code
  resendCode() {
    if (this.resendDisabled) return;

    this.authenticationService.resendVerificationCode()
      .pipe(first())
      .subscribe(
        (data) => {
          this.verificationError = '';
          this.startResendCooldown();
          // Show success message briefly
          this.verificationError = 'Codul a fost retrimis pe email.';
          setTimeout(() => {
            if (this.verificationError === 'Codul a fost retrimis pe email.') {
              this.verificationError = '';
            }
          }, 3000);
        },
        (error) => {
          this.verificationError = 'Nu s-a putut retrimite codul. Încercați din nou.';
        }
      );
  }

  // Start cooldown for resend button
  startResendCooldown() {
    this.resendDisabled = true;
    this.resendCountdown = 60;

    const interval = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.resendDisabled = false;
        clearInterval(interval);
      }
    }, 1000);
  }

  // Go back to login form
  backToLogin() {
    this.requiresVerification = false;
    this.requiresTOTP = false;  // ADD THIS
    this.requires2FASetup = false;
    this.verificationError = '';
    this.setupError = '';
    this.deviceType = null;
    this.replacingDevice = null;
    this.setupFlowToken = null;
    this.setupData = null;
    this.backupCodes = [];
    this.showBackupCodes = false;
    this.authenticationService.cancelVerification();
    this.loginForm.patchValue({ password: '' });
  }

  // Copy secret for manual entry
  copySecret() {
    if (this.setupData?.secret) {
      navigator.clipboard.writeText(this.setupData.secret);
    }
  }

  public goTop() {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }

  getReplacementWarning(): string {
    if (this.deviceType === 'desktop') {
      return 'Înțeleg că vrei să folosești aplicația DoctorBusiness.ro de pe un alt calculator/laptop. Pentru securitatea contului tău, acest dispozitiv nou va înlocui calculatorul salvat anterior. Dacă vei încerca să te conectezi de pe un alt calculator în următoarele 30 de zile, accesul va fi blocat și va trebui să contactezi echipa de suport.';
    } else {
      return 'Înțeleg că vrei să folosești aplicația DoctorBusiness.ro de pe un alt telefon/tabletă. Pentru securitatea contului tău, acest dispozitiv nou va înlocui telefonul salvat anterior. Dacă vei încerca să te conectezi de pe un alt telefon în următoarele 30 de zile, accesul va fi blocat și va trebui să contactezi echipa de suport.';
    }
  }
}
